import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Comment } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { allLanguageCommentsQueryKey } from './use-comments-query';

type UseCommentLanguageSummaryQueryParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: number;
};

const commentLanguageSummaryQueryKey = ({ architectureId, itemId, version }: UseCommentLanguageSummaryQueryParams) =>
    allLanguageCommentsQueryKey({ architectureId, itemId, version });

const countBySourceLanguage = (comments: readonly Comment[]): ReadonlyMap<string, number> => {
    const counts = new Map<string, number>();
    for (const comment of comments) {
        if (!is.nullOrUndefined(comment.resolution)) continue;
        counts.set(comment.sourceLanguage, (counts.get(comment.sourceLanguage) ?? 0) + 1);
    }
    return counts;
};

export const useCommentLanguageSummaryQuery = ({
    architectureId,
    itemId,
    version,
}: UseCommentLanguageSummaryQueryParams): UseQueryResult<ReadonlyMap<string, number>, Error> => {
    const { fetchComments } = useApplicationFetchers();

    return useQuery<readonly Comment[], Error, ReadonlyMap<string, number>>({
        queryFn: async () => {
            const result = await fetchComments({ architectureId, itemId, version });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        queryKey: commentLanguageSummaryQueryKey({ architectureId, itemId, version }),
        select: countBySourceLanguage,
        throwOnError: false,
    });
};
